/* eslint-disable max-lines */
import {
  AttachmentInterface,
  AttachmentTypeEnum,
  AttributeNameEnum,
  AuctionInfoInterface,
  AuctionInfoWithItemIdInterface,
  AuctionOfferAddedInterface,
  AuctionSoldInterface,
  AuctionTypeEnum,
  CartAuctionInterface,
  CategoryAttributeInterface,
  CategoryWithAttributesInterface,
  DeliveryOptionInterface,
  ItemAttributeInterface,
  ListPaginationInterface,
  SettingsInterface,
  StepPriceInterface,
} from '@on-arte/core-types';
import {
  ArtworkSummaryTheme,
  AttributeWithValue,
  BoxSlide,
  getPathWithParams,
  Language,
  useFormatDate,
  UseFormatDate,
  UseLogger,
  useLogger,
  UseNotifications,
  useNotifications,
  UseParsers,
  useParsers,
  UseRedirect,
  useRedirect,
  UseState
} from '@on-arte/ui';
import i18next from 'i18next';
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { QueryClient, useQueries, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { 
  addCartItem, 
  getAuctionDetails,
  getAuctions, 
  getItemsCategories, 
  getSettings, 
  makeOffer 
} from '@onArte/frontend/api/requests';
import { appConfig } from '@onArte/frontend/app.config';
import { ArtworkDescriptionEnum, FrontendResponseCode, QueryKey } from '@onArte/frontend/enums';
import {
  useAuth,
  useBasket,
  useConversions,
  useItemsHelpers,
  useObjectsTransformations,
  useRouteInfo,
  useSocket
} from '@onArte/frontend/hooks';
import {
  ItemsHelpers,
  RoutingState,
  UseAuth,
  UseBasket,
  UseConversions,
  UseObjectsTranformations,
  UseSocket
} from '@onArte/frontend/interfaces';
import { FrontendApiError } from '@onArte/frontend/models';
import { emptyRequest, findAttributeValueByName, findStepPriceElement } from '@onArte/frontend/utils';
import { RouteNameEnum, WebSocketCommand } from '@onArte/shared/enums';
import { AuctionPublicDetails, RouteInfo } from '@onArte/shared/interfaces';
import { WebSocketSubscriptionDetails } from '@onArte/shared/models';
import {
  artistWorksLimit,
  artworkTitleSuffixQueryKey,
  ArtworkNamesake,
  buildArtworkCanonicalPath,
  buildArtworkDistinctiveFeatures,
  buildArtworkTitleSuffix,
  findNamesakeAuctionIds,
  isArtworkDetailsRoute,
  stripArtworkCategoryPath,
  buildArtworkImageAlt,
  buildArtworkMetaSource,
  buildArtworkTitle,
  getRouteDetailsByName,
  TranslateOrEmpty
} from '@onArte/shared/utils';

import { BiddingExamples, UseArtworkDetails } from './artworkDetails.types';

// Dopisek w tytule musi wyjść identycznie jak na serwerze, więc etykiety techniki i podłoża
// tłumaczymy tą samą regułą co `ssr.helper.tsx`: słownik wprost, pusty ciąg przy braku klucza.
// `t()` oddaje przy braku klucza sam klucz, co rozjechałoby tytuł karty z tytułem z serwera.
//
// Słownik czytamy z i18next, który obie strony ładują przy starcie, a NIE importem pliku JSON:
// ten widok renderuje też serwer, a w obrazie produkcyjnym nie ma `src/`, tylko skompilowany
// `build/`, do którego kompilator plików JSON nie kopiuje. Import pliku wywracał start serwisu.
const translateOrEmpty: TranslateOrEmpty = (key: string): string => (
  i18next.getResourceBundle('pl', 'translation') as Record<string, string> | undefined
)?.[key] ?? '';

export const useArtworkDetails: (artworkId?: string, currentRouteObject?: RouteInfo) => UseArtworkDetails = (
  artworkId: string = '', currentRouteObject?: RouteInfo
): UseArtworkDetails => {
  // ── Renderowanie po stronie serwera ────────────────────────────────────────
  // Klucz [AuctionDetails, artworkId] da się odtworzyć na serwerze, bo zależy
  // wyłącznie od identyfikatora z adresu — dane są więc zasiane pod nim wprost
  // (ssrPrefetch.helper.ts). Ale `onSuccess`, które normalnie przepisuje je do
  // stanu lokalnego, przy renderowaniu serwerowym się nie wykonuje.
  //
  // Dlatego zamiast dokładać wartości zastępcze w kilkunastu miejscach, bierzemy
  // dane z pamięci podręcznej już jako stan początkowy. Reszta hooka i cały widok
  // działają wtedy bez żadnej zmiany.
  const queryClient: QueryClient = useQueryClient();
  const ssrArtworkDetails: AuctionPublicDetails | undefined = queryClient
    .getQueryData<AuctionPublicDetails>([QueryKey.AuctionDetails, artworkId]);

  const [artworkDetailsData, setArtworkDetailsData]: UseState<AuctionPublicDetails | null>
    = useState<AuctionPublicDetails | null>(ssrArtworkDetails ?? null);
  const [artworkPhotos, setArtworkPhotos]: UseState<string[]> = useState<string[]>(
    (): string[] => ssrArtworkDetails
      ? ssrArtworkDetails.itemDetails.attachments
        .filter((attachment: AttachmentInterface): boolean => [
          AttachmentTypeEnum.ItemPhoto,
          AttachmentTypeEnum.ItemCertificate,
          AttachmentTypeEnum.ItemSignature,
        ].includes(attachment.type))
        .map((attachment: AttachmentInterface): string => attachment.path)
      : []
  );
  const [commissionPercent, setCommissionPercent]: UseState<number> = useState<number>(0);
  const [popupValidationMessage, setPopupValidationMessage]: UseState<string> = useState<string>('');
  const [stepPrices, setStepPrices]: UseState<StepPriceInterface[]> = useState<StepPriceInterface[]>([]);
  const [categories, setCategories]: UseState<CategoryWithAttributesInterface[]> = useState<CategoryWithAttributesInterface[]>([]);
  const [authorMoreArtworks, setAuthorMoreArtworks]: UseState<BoxSlide[]> = useState<BoxSlide[]>([]);
  const { logger }: UseLogger = useLogger();
  const { getAttributeValueByType }: ItemsHelpers = useItemsHelpers();
  const { t }: TransProps<never> = useTranslation();
  const { redirect }: UseRedirect = useRedirect();
  const { addToast }: UseNotifications = useNotifications();
  const { isUserLoggedIn, userData }: UseAuth = useAuth();
  const { cartData }: UseBasket = useBasket();
  const { transformAuctionToBoxSlideType }: UseObjectsTranformations = useObjectsTransformations();
  const { parsePrice }: UseParsers = useParsers();
  const [isPopupVisible, setIsPopupVisible]: UseState<boolean> = useState<boolean>(false);
  const [inputValue, setInputValue]: UseState<string> = useState<string>('');
  const [artworkSummaryTheme, setArtworkSummaryTheme]: UseState<ArtworkSummaryTheme>
    = useState<ArtworkSummaryTheme>(
      ssrArtworkDetails?.type === AuctionTypeEnum.PriceProposal
        ? ArtworkSummaryTheme.MakeOffer
        : ssrArtworkDetails?.type === AuctionTypeEnum.Bidding
          ? ArtworkSummaryTheme.Auction
          : ArtworkSummaryTheme.BuyNow
    );
  const offerPrice: number = useMemo((): number => parseFloat(inputValue), [inputValue]);
  const { socket, connectedToSocket, setIsConnectedToSocket }: UseSocket = useSocket();
  const { formatDate }: UseFormatDate = useFormatDate(Language.Pl);
  const auctionsIdsToNotUnsubscribe: MutableRefObject<string[]> = useRef([]);
  const currency: string = 'zł';
  const { sendAddToCartEvent }: UseConversions = useConversions();

  useEffect(
    (): void => {
      if (!socket || connectedToSocket || !artworkDetailsData || !artworkId || !isArtworkDetailsRoute(currentRouteObject?.name)) {
        return;
      }

      setIsConnectedToSocket();
      socket.emit(WebSocketCommand.SubscribeAuction, { id: artworkId } as WebSocketSubscriptionDetails);
      socket.on(WebSocketCommand.AuctionOfferAdded, (data: AuctionOfferAddedInterface): void => {
        setArtworkDetailsData({ ...artworkDetailsData, price: data.offer.price });
        if (data.offer.user.id !== userData?.id) {
          addToast({ content: t('onarte.website.artworkDetailsView.auctionOfferAdded', { price: `${data.offer.price} ${currency}`}) });
        }
      });
      socket.on(WebSocketCommand.AuctionOfferUpdatedEndDatetime, (data: AuctionInfoInterface): void => {
        if (data.validity?.to) {
          setArtworkDetailsData({ ...artworkDetailsData, validity: data.validity });
          addToast({
            content: t(
              'onarte.website.artworkDetailsView.auctionOfferUpdatedEndDatetime',
              { date: formatDate(data.validity.to, 'DD.MM.YY HH:mm:ss') }
            )
          });
        }
      });
      socket.on(WebSocketCommand.AuctionSold, (data: AuctionSoldInterface): void => {
        void auctionDetailsQuery.refetch().then((): void => {
          const translationKeyType: string = data.auction.type === AuctionTypeEnum.Bidding ? 'bidding' : 'otherSales';
          addToast({
            content: t(
              data.buyer.id === userData?.id
                ? `onarte.website.artworkDetailsView.auctionSold.${translationKeyType}.toMe`
                : `onarte.website.artworkDetailsView.auctionSold.${translationKeyType}.toOther`,
              { name: data.auction.name }
            )
          });
        });
      });
    },
    [socket, artworkDetailsData, artworkId, currentRouteObject]
  );

  useEffect(
    (): (() => void) => {
      return (): void => {
        if (socket) {
          if (!auctionsIdsToNotUnsubscribe.current.includes(artworkId)) {
            socket.emit(WebSocketCommand.UnsubscribeAuction, { id: artworkId } as WebSocketSubscriptionDetails);
          }
        }
      };
    },
    [socket]
  );

  useEffect(
    (): (() => void) => {
      return (): void => {
        if (socket) {
          socket.off(WebSocketCommand.AuctionOfferAdded);
          socket.off(WebSocketCommand.AuctionOfferUpdatedEndDatetime);
          socket.off(WebSocketCommand.AuctionSold);
        }
      };
    },
    [socket]
  );

  useEffect(
    (): void => {
      if (cartData) {
        auctionsIdsToNotUnsubscribe.current = cartData.auctions
          .map((cartAuction: CartAuctionInterface): string => cartAuction.auction.auctionId);
      }
    },
    [cartData?.auctions]
  );

  useQuery(
    [QueryKey.ItemCategories],
    (): Promise<CategoryWithAttributesInterface[]> => getItemsCategories(),
    {
      onSuccess: (data: CategoryWithAttributesInterface[]): void => setCategories(data),
      onError: (error: FrontendApiError): void => logger(QueryKey.ItemCategories, error)
    }
  );

  useQuery(
    [QueryKey.Settings],
    (): Promise<SettingsInterface> => getSettings(),
    {
      onSuccess: (data: SettingsInterface): void => {
        setCommissionPercent(data.commissionForBidding);
        setStepPrices(data.stepPrices);
      },
      onError: (error: FrontendApiError): void => logger(QueryKey.Settings, error)
    }
  );

  const getAttachmentsByType: (attachments: AttachmentInterface[], type: AttachmentTypeEnum) => AttachmentInterface[] = (
    attachments: AttachmentInterface[], type: AttachmentTypeEnum
  ): AttachmentInterface[] => {
    return attachments.filter((attachment: AttachmentInterface): boolean => attachment.type === type);
  };

  const auctionDetailsQuery: UseQueryResult = useQuery(
    [QueryKey.AuctionDetails, artworkId],
    (): Promise<AuctionPublicDetails | void> => artworkId && isArtworkDetailsRoute(currentRouteObject?.name)
      ? getAuctionDetails(artworkId)
      : emptyRequest(),
    {
      onSuccess: (data: AuctionPublicDetails | undefined): void => {
        if (!data) {
          return;
        }

        const photos: AttachmentInterface[] = [
          ...getAttachmentsByType(data.itemDetails.attachments, AttachmentTypeEnum.ItemPhoto),
          ...getAttachmentsByType(data.itemDetails.attachments, AttachmentTypeEnum.ItemCertificate),
          ...getAttachmentsByType(data.itemDetails.attachments, AttachmentTypeEnum.ItemSignature),
        ];
        setArtworkPhotos(photos.map((attachment: AttachmentInterface): string => attachment.path));
        setArtworkDetailsData(data);
        setArtworkSummaryTheme(data.type === AuctionTypeEnum.PriceProposal
          ? ArtworkSummaryTheme.MakeOffer
          : data.type === AuctionTypeEnum.Bidding
            ? ArtworkSummaryTheme.Auction
            : ArtworkSummaryTheme.BuyNow
        );
      },
      onError: (error: FrontendApiError): void => {
        logger(QueryKey.AuctionDetails, error);
        if (error.responseCode === FrontendResponseCode.NotFound) {
          redirect(getRouteDetailsByName(RouteNameEnum.NotFound)?.url ?? '/');
          return;
        } else {
          addToast({ content: t(error.message) });
        }
      }
    }
  );

  // Widok nie jest montowany od nowa przy przejściu między dziełami tej samej kategorii, więc przez
  // chwilę `artworkDetailsData` trzyma POPRZEDNIE dzieło, a `artworkId` już nowe. Bez warunku
  // zgodności lista poprzedniego artysty trafiała pod klucz nowego dzieła i zostawała tam na stałe:
  // dopisek w tytule wypadał, a suwak „inne prace artysty" pokazywał prace niewłaściwej osoby.
  // Identyfikator artysty w kluczu wymusza nowe pobranie, gdy szczegóły nowego dzieła dojadą.
  const detailsMatchRoute: boolean = !!artworkId && artworkDetailsData?.id === artworkId;
  const artistId: string | undefined = artworkDetailsData?.itemDetails.manufacturer?.id;
  const artistAuctionsQuery: UseQueryResult<ListPaginationInterface<AuctionInfoWithItemIdInterface>> = useQuery(
    [QueryKey.AuctionsList, artworkId, artistId],
    (): Promise<ListPaginationInterface<AuctionInfoWithItemIdInterface>> => getAuctions(
      { limit: artistWorksLimit, offset: 0, manufacturerIds: [artistId ?? ''] }
    ),
    {
      enabled: !!artistId && detailsMatchRoute,
      onSuccess: (data: ListPaginationInterface<AuctionInfoWithItemIdInterface>): void => setAuthorMoreArtworks(
        data.list
          .filter((auction: AuctionInfoInterface): boolean => auction.id !== artworkId)
          .map(transformAuctionToBoxSlideType)
      ),
      onError: (error: FrontendApiError): void => logger(QueryKey.AuctionsList, error),
    }
  );

  // ── Dopisek w tytule przy dziełach o tej samej nazwie (poz. 2) ─────────────
  // Grupa i dopisek liczone tą samą funkcją co na serwerze (ssrQueries.helper.ts), na tych
  // samych danych: te same parametry listy i te same szczegóły dzieł z grupy.
  const namesakeIds: string[] = useMemo(
    (): string[] => artworkDetailsData && artistAuctionsQuery.data
      ? findNamesakeAuctionIds(artworkDetailsData, artistAuctionsQuery.data.list)
      : [],
    [artworkDetailsData, artistAuctionsQuery.data]
  );

  const namesakeQueries: UseQueryResult<AuctionPublicDetails>[] = useQueries(
    namesakeIds
      .filter((id: string): boolean => id !== artworkDetailsData?.id)
      .map((id: string) => ({
        queryKey: [QueryKey.AuctionDetails, id],
        queryFn: (): Promise<AuctionPublicDetails> => getAuctionDetails(id),
      }))
  ) as UseQueryResult<AuctionPublicDetails>[];

  // Dopisek, którego serwer użył w tytule strony wejścia (zasiew SSR); `undefined` dla dzieł otwartych
  // przejściem wewnątrz aplikacji. Zasiew OBSERWUJEMY, a nie czytamy `getQueryData`: zapytanie bez
  // obserwatora react-query usuwa po 5 minutach (domyślny `cacheTime`), więc przy nieudanym pobraniu
  // najbliższe renderowanie (np. otwarcie okienka oferty) skasowałoby dopisek z tytułu. `enabled: false`
  // — nic nie pobieramy, tylko trzymamy wpis przy życiu i reagujemy na zmianę dzieła.
  const { data: ssrTitleSuffix }: UseQueryResult<string | null> = useQuery<string | null>(
    artworkTitleSuffixQueryKey(artworkId),
    (): Promise<string | null> => Promise.resolve(null),
    { enabled: false }
  );

  // `undefined` znaczy „jeszcze nie wiadomo" — wtedy tytułu nie ruszamy. Póki własne dane są w drodze,
  // obowiązuje dopisek z serwera; przy błędzie pobrania (ponawianie jest wyłączone) też zostajemy
  // przy nim, a gdy serwer tej strony nie renderował — przy samym szablonie. Nigdy nie kasujemy
  // poprawnego dopisku z serwera tylko dlatego, że zapytanie w przeglądarce się nie udało.
  const titleSuffix: string | null | undefined = useMemo(
    (): string | null | undefined => {
      if (!artworkDetailsData || !detailsMatchRoute) {
        return undefined;
      }
      const onFailure: string | null = ssrTitleSuffix ?? null;
      if (!artistId) {
        return null;
      }
      if (artistAuctionsQuery.isError) {
        return onFailure;
      }
      if (!artistAuctionsQuery.data) {
        return ssrTitleSuffix;
      }
      if (namesakeIds.length < 2) {
        return null;
      }
      if (namesakeQueries.some((query: UseQueryResult<AuctionPublicDetails>): boolean => query.isLoading)) {
        return ssrTitleSuffix;
      }

      const group: ArtworkNamesake[] = namesakeIds
        .map((id: string): AuctionPublicDetails | undefined => id === artworkDetailsData.id
          ? artworkDetailsData
          : namesakeQueries.find((query: UseQueryResult<AuctionPublicDetails>): boolean => query.data?.id === id)?.data)
        .filter((member: AuctionPublicDetails | undefined): member is AuctionPublicDetails => !!member)
        .map((member: AuctionPublicDetails): ArtworkNamesake => ({
          id: member.id,
          features: buildArtworkDistinctiveFeatures(member, translateOrEmpty),
        }));

      return group.length === namesakeIds.length ? buildArtworkTitleSuffix(group, artworkDetailsData.id) : onFailure;
    },
    [
      artworkDetailsData, detailsMatchRoute, ssrTitleSuffix, artistId,
      artistAuctionsQuery.data, artistAuctionsQuery.isError, namesakeIds, namesakeQueries,
    ]
  );

  // Tytuł ustawia widok, bo `AppWrapper` celowo go tu nie dotyka — patrz
  // `routesManagingOwnTitle`. Ten sam szablon i ten sam dopisek liczy serwer, więc karta
  // przeglądarki pokazuje dokładnie to, co dostaje wyszukiwarka.
  useEffect(
    (): void => {
      if (!artworkDetailsData || titleSuffix === undefined) {
        return;
      }

      document.title = buildArtworkTitle({
        itemName: artworkDetailsData.itemDetails.name,
        artistName: artworkDetailsData.itemDetails.manufacturer?.name
          ?? artworkDetailsData.publicProfile?.name
          ?? appConfig.applicationName,
        titleSuffix,
      });
    },
    [artworkDetailsData, titleSuffix]
  );

  const categoryAttributes: CategoryAttributeInterface[] = useMemo((): CategoryAttributeInterface[] => {
    return categories.find((category: CategoryWithAttributesInterface) => (
      category.name === artworkDetailsData?.itemDetails.category.name)
    )?.attributes ?? [];
  }, [artworkDetailsData?.itemDetails.category, categories]);

  const attributesWithValues: AttributeWithValue[] = useMemo((): AttributeWithValue[] => {
    return artworkDetailsData?.itemDetails?.attributes?.map((attribute: ItemAttributeInterface): AttributeWithValue => {
      const attributeProperties: CategoryAttributeInterface | undefined = categoryAttributes.find(
        (attributeItem: CategoryAttributeInterface): boolean => attributeItem.id === attribute.attributeId
      );

      return {
        label: t(attributeProperties?.label ?? ''),
        value: attributeProperties
          ? getAttributeValueByType(attribute.value, attributeProperties)
          : '',
      };
    }) ?? [];
  }, [categoryAttributes, artworkDetailsData]);

  const artworkDimensions: string = useMemo((): string => {
    const height: string | undefined = findAttributeValueByName(
      artworkDetailsData?.itemDetails.attributes ?? [],
      categoryAttributes,
      AttributeNameEnum.Height
    );
    const width: string | undefined = findAttributeValueByName(
      artworkDetailsData?.itemDetails.attributes ?? [],
      categoryAttributes,
      AttributeNameEnum.Width
    );

    return height && width
      ? `${height} x ${width}cm`
      : '';
  }, [artworkDetailsData, categoryAttributes]);

  const buyNowAction: () => void = (): void => {
    if (isUserLoggedIn()) {
      if (cartData && artworkDetailsData) {
        addCartItem(cartData.id, artworkDetailsData.id)
          .then((): void => {
            sendAddToCartEvent(artworkDetailsData.id, cartData.id, artworkDetailsData.itemDetails.name, artworkDetailsData.price ?? null);
            redirect(getPathWithParams(getRouteDetailsByName(RouteNameEnum.CartDelivery)?.url ?? '/', { id: cartData.id ?? '' }));
          })
          .catch((error: FrontendApiError): void => addToast({ content: t(error.message) }));
      }
    } else {
      redirect(getRouteDetailsByName(RouteNameEnum.SignIn)?.url ?? '/', { withReplaceState: true });
    }
  };

  const openPopupAction: (value?: string) => void = (value?: string): void => {
    if (isUserLoggedIn()) {
      if (value && artworkDetailsData?.type === AuctionTypeEnum.Bidding) {
        setInputValue(value);
        setIsPopupVisible(true);
      } else if (artworkDetailsData?.type === AuctionTypeEnum.PriceProposal) {
        setIsPopupVisible(true);
      }
    } else {
      redirect(getRouteDetailsByName(RouteNameEnum.SignIn)?.url ?? '/', { withReplaceState: true });
    }
  };

  const getBiddingExamples: (stepPrice: StepPriceInterface | undefined) => BiddingExamples = (
    stepPrice: StepPriceInterface | undefined
  ): BiddingExamples => {
    if (artworkDetailsData?.price && stepPrice) {
      const lowerDivisibleValue: number = Math.floor(offerPrice / stepPrice.value) * stepPrice.value;
      const higherDivisibleValue: number = Math.ceil(offerPrice / stepPrice.value) * stepPrice.value;
      if (lowerDivisibleValue > artworkDetailsData.price) {
        return { lowerValue: lowerDivisibleValue, higherValue: higherDivisibleValue, stepPrice: stepPrice.value };
      } else {
        return { lowerValue: higherDivisibleValue, higherValue: higherDivisibleValue + stepPrice.value, stepPrice: stepPrice.value };
      }
    } else {
      return { lowerValue: 0, higherValue: 0, stepPrice: 0 };
    }
  };

  const makeBiddingOfferAction: () => void = (): void => {
    if (artworkId && offerPrice && artworkDetailsData?.price) {
      const stepPrice: StepPriceInterface | undefined = findStepPriceElement(stepPrices, offerPrice);
      const biddingExamples: BiddingExamples = getBiddingExamples(stepPrice);
      
      if (offerPrice <= artworkDetailsData.price) {
        setPopupValidationMessage(t('onarte.website.artworkDetailsView.wrongBiddingValue'));
      } else if (stepPrice && offerPrice % stepPrice.value !== 0) {
        setPopupValidationMessage(`${t('onarte.website.artworkDetailsView.notDivisibleByStepPrices', biddingExamples)}`);
      } else {
        makeOffer(artworkId, { price: offerPrice })
          .then((): void => {
            addToast({ content: t('onarte.website.artworkDetailsView.offerPrice', { currency, value: offerPrice }) });
            setIsPopupVisible(false);
            redirect(getRouteDetailsByName(RouteNameEnum.UserBiddingsList)?.url ?? '/');
          })
          .catch((error: FrontendApiError): void => {
            setPopupValidationMessage(t(error.message));
          });
      }
    }
  };

  const makePriceProposalOfferAction: () => void = (): void => {
    if (!offerPrice) {
      setPopupValidationMessage(t('onarte.website.artworkDetailsView.tooSmallValue'));
    }

    if (artworkId && offerPrice && artworkDetailsData) {      
      makeOffer(artworkId, { price: offerPrice })
        .then((): void => {
          addToast({ content: t('onarte.website.artworkDetailsView.offerPrice', { currency, value: offerPrice }) });
          setIsPopupVisible(false);
        })
        .catch((error: FrontendApiError): void => {
          setPopupValidationMessage(t(error.message));
        });
    }
  };

  const onPopupInputChange: (value: string) => void = (value: string): void => {
    setInputValue(value);
    setPopupValidationMessage('');
  };

  const nextPrice: number | undefined = useMemo((): number | undefined => {
    if (!artworkDetailsData?.price) {
      return;
    }

    const stepPriceElement: StepPriceInterface | undefined = findStepPriceElement(stepPrices, artworkDetailsData.price);
    if (stepPriceElement) {
      return Math.round((artworkDetailsData.price + stepPriceElement.value) / stepPriceElement.value) * stepPriceElement.value;
    }
    // `stepPrices` MUSI być w zależnościach: przy renderowaniu po stronie serwera cena
    // jest znana już przy pierwszym renderowaniu, a progi licytacji dojeżdżają dopiero
    // z ustawieniami. Bez tego wyliczenie nigdy się nie powtarza, pole kwoty zostaje
    // puste i przycisk licytacji nic nie robi.
  }, [artworkDetailsData?.price, stepPrices]);

  const artworkDescriptionAttributes: Record<ArtworkDescriptionEnum, AttributeWithValue> = useMemo(
    (): Record<ArtworkDescriptionEnum, AttributeWithValue> => ({
      [ArtworkDescriptionEnum.Location]: {
        label: t('onarte.website.useArtworkDetails.artworkDescriptionAttributes.location.label'),
        value: artworkDetailsData?.itemDetails.location.address.city ?? ''
      },
      // TODO update with api
      [ArtworkDescriptionEnum.Contact]: {
        label: t('onarte.website.useArtworkDetails.artworkDescriptionAttributes.contact.label'),
        value: '+48 61 111 0183'
      },
      [ArtworkDescriptionEnum.Return]: {
        label: t('onarte.website.useArtworkDetails.artworkDescriptionAttributes.return.label'),
        value: '14 dni na zwrot'
      },
      [ArtworkDescriptionEnum.DeliveryTime]: {
        label: t('onarte.website.useArtworkDetails.artworkDescriptionAttributes.deliveryTime.label'),
        value: '48h od momentu nadania'
      },
      [ArtworkDescriptionEnum.Delivery]: {
        label: t('onarte.website.useArtworkDetails.artworkDescriptionAttributes.delivery.label'),
        value: artworkDetailsData?.itemDetails.deliveryOptions
          .map((delivery: DeliveryOptionInterface): string => `${t(delivery.name)}: ${parsePrice(delivery.price)} zł`)
          .join('\n') ?? '',
      },
      [ArtworkDescriptionEnum.Commission]: {
        label: t('onarte.website.useArtworkDetails.artworkDescriptionAttributes.commission.label'),
        value: t('onarte.website.useArtworkDetails.artworkDescriptionAttributes.commission.value')
      }
    }),
    [artworkDetailsData]
  );

  // Adres w pasku przeglądarki ma odpowiadać adresowi docelowemu z kategorią
  // (poz. 13) także przy nawigacji wewnątrz aplikacji — kafle linkują formę bez
  // segmentu, a nawigacja w aplikacji nie przechodzi przez serwerowe 301.
  // Podmiana `replace`, żeby przycisk „wstecz" nie przechodził przez oba adresy.
  const location: Location = useLocation();
  const navigate: NavigateFunction = useNavigate();
  const { currentWebsiteUrl }: RoutingState = useRouteInfo();
  useEffect(
    (): void => {
      const prettyUrl: string | undefined = currentWebsiteUrl?.prettyUrl;
      if (!artworkDetailsData || !prettyUrl) {
        return;
      }

      // Stan trasy może opisywać jeszcze POPRZEDNIE dzieło: efekty widoku wykonują się
      // przed efektem `AppWrapper`, który go czyści i ustala na nowo. Bez tego warunku
      // klik w kafel innego dzieła był cofany na dzieło wyjściowe.
      if (stripArtworkCategoryPath(location.pathname) !== prettyUrl) {
        return;
      }

      const targetPath: string = buildArtworkCanonicalPath(prettyUrl, artworkDetailsData.itemDetails.category?.name);
      if (targetPath !== location.pathname) {
        navigate({ pathname: targetPath, search: location.search }, { replace: true });
      }
    },
    [artworkDetailsData?.id, currentWebsiteUrl?.prettyUrl, location.pathname]
  );

  // Alt zdjęć dzieła wg szablonu z poz. 7 — ten sam, który serwer wysyła w og:image:alt,
  // więc tłumaczony tą samą regułą co tytuł (`translateOrEmpty` na górze pliku).
  const artworkImageAlt: string = useMemo(
    (): string => artworkDetailsData
      ? buildArtworkImageAlt(buildArtworkMetaSource(artworkDetailsData, translateOrEmpty))
      : '',
    [artworkDetailsData]
  );

  return {
    artworkSummaryTheme,
    attributesWithValues,
    artworkDimensions,
    artworkImageAlt,
    buyNowAction,
    openPopupAction,
    makeBiddingOfferAction,
    makePriceProposalOfferAction,
    onPopupInputChange,
    nextPrice,
    popupValidationMessage,
    artworkDetailsData,
    artworkPhotos,
    offerPrice,
    commissionPercent,
    isPopupVisible,
    setIsPopupVisible,
    inputValue,
    authorMoreArtworks,
    artworkDescriptionAttributes
  };
};
